/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Revert Reconcile Payment Button
 */
import { useState } from 'react';
import { Button, ButtonProps, Descriptions, Flex, Input, Modal } from 'antd';
import { commentLabel } from './ReconcilePayment';
import useUpdateERA from 'hooks/payments/mutate/useUpdateERA';
import { formatAmount } from 'utils/accounts/allAccounts';
import useUpdateEntityNote from 'hooks/mutate/useUpdateEntityNote';
import { PAYMENT_RECONCILIATION_OUTCOME, PAYMENT_RECONCILIATION_STATUS } from 'app-constants/claims/era';
import { EMPTY_VALUE } from '@veritable/common/constants';

export interface IProps extends ButtonProps {
	data: ERA.ERADetailsResponse | ERA.ERAListResponse;
}
/**
 * Revert Reconcile Payment button
 * @param props
 * @returns {React.FC} Revert Reconcile Payment
 */

const RevertReconcilePayment: React.FC<IProps> = ({ data, ...props }) => {
	const status = data?.fhirResource?.status;
	const outcome = data?.fhirResource?.outcome;
	const canRevertERA =
		status === PAYMENT_RECONCILIATION_STATUS.active &&
		(outcome === PAYMENT_RECONCILIATION_OUTCOME.complete || outcome === PAYMENT_RECONCILIATION_OUTCOME.partial);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [note, setNote] = useState('');
	const { mutate: updateERA, isPending } = useUpdateERA();
	const { mutate: updateNote, isPending: isUpdatingNote } = useUpdateEntityNote();

	const fhirResource = data?.fhirResource;
	const loading = isPending || isUpdatingNote;

	const handleCancel = () => {
		setIsModalOpen(false);
		setNote('');
	};
	const handleOk = () => {
		updateERA(
			{
				requestParams: {
					paymentReconciliationId: data?.fhirResource?.id,
					eRAPatchModel: {
						meta: data?.fhirResource?.meta,
						status: PAYMENT_RECONCILIATION_STATUS.active,
						outcome: PAYMENT_RECONCILIATION_OUTCOME.queued,
					},
				},
			},
			{
				onSuccess: () => {
					if (note) {
						updateNote(
							{
								entityType: fhirResource?.resourceType,
								entityId: fhirResource?.id,
								notePatchModel: { note, id: data?.userNote?.id },
							},
							{
								onSuccess: () => {
									handleCancel();
								},
							},
						);
					} else {
						handleCancel();
					}
				},
			},
		);
	};
	if (canRevertERA) {
		return (
			<>
				<Button type='text' danger onClick={() => setIsModalOpen(true)} {...props} data-testid='ipr-revert-btn'>
					{'Revert'}
				</Button>
				<Modal
					title={'Confirm Insurance Payment (ERA) Revert'}
					destroyOnHidden
					open={isModalOpen}
					onCancel={handleCancel}
					onOk={handleOk}
					width={560}
					okText={'Confirm'}
					data-testid='ipr-revert-modal'
					okButtonProps={{ loading, 'data-testid': 'ipr-revert-confirm' }}
					cancelButtonProps={{ disabled: loading, 'data-testid': 'ipr-revert-cancel' }}
				>
					<Flex vertical gap={16}>
						{'Please review the details below. Reverting will clear the reconciliation.'}
						<Descriptions layout='vertical' column={2} colon={false} className='allocation-overview-descriptions'>
							<Descriptions.Item label={'Remittance Number'}>{data?.metadata?.eraNumber || EMPTY_VALUE}</Descriptions.Item>
							<Descriptions.Item label={'Total Paid Amount'}>
								{formatAmount(fhirResource?.amount?.value) || EMPTY_VALUE}
							</Descriptions.Item>
						</Descriptions>
						<Flex vertical gap={8}>
							{commentLabel}
							<Input.TextArea
								placeholder={'Enter your comment here (optional)'}
								value={note}
								onChange={(e) => setNote(e.target.value)}
								data-testid='ipr-revert-note'
							/>
						</Flex>
					</Flex>
				</Modal>
			</>
		);
	}
	return null;
};

export default RevertReconcilePayment;
